import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FolderIcon, PlusIcon } from "../Components/Icons";
import { LIMITS, isAdmin } from "../constants.js";
import { countAssetsByBrand } from "../lib/generate/countAssetsByBrand.js";
import "./Brands.css";

export default function Brands({ brandConfig, role, config }) {
  const navigate = useNavigate();
  const canCreate = isAdmin(role);

  // A brand.id of null means this entry is the unconfigured placeholder,
  // not a real brand — filter those out before counting/rendering.
  const brands = (brandConfig ?? []).filter((entry) => entry.brand.id !== null);
  const atLimit = brands.length >= LIMITS.MAX_BRANDS_PER_WORKSPACE;

  // Real per-brand asset counts, fetched in one round trip (same action
  // the Usage page already uses) - nothing in brandConfig itself carries
  // this, since assets aren't eagerly loaded for every brand upfront the
  // way references are.
  const [assetCounts, setAssetCounts] = useState({});
  useEffect(() => {
    if (brands.length === 0) return;
    let cancelled = false;
    countAssetsByBrand(
      config,
      brands.map((b) => b.brand.id),
      () => {},
      () => {}
    )
      .then((counts) => {
        if (!cancelled) setAssetCounts(counts);
      })
      .catch(() => {
        // Non-critical - cards just show 0 rather than blocking the page.
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [brands.length]);

  return (
    <div className="brands-page">
      <div className="brands-page__header">
        <div>
          <h1 className="brands-page__title">Brands</h1>
          <p className="brands-page__subtitle">
            {brands.length} of {LIMITS.MAX_BRANDS_PER_WORKSPACE} brands
          </p>
        </div>
        {canCreate ? (
          <button
            type="button"
            onClick={() => navigate("/brands/new")}
            disabled={atLimit}
            className="btn btn--primary brands-page__new-btn"
            title={atLimit ? "Workspace brand limit reached" : undefined}
          >
            <PlusIcon className="brands-page__new-icon" />
            New brand
          </button>
        ) : null}
      </div>

      {brands.length === 0 ? (
        <div className="brands-page__empty">
          <FolderIcon className="brands-page__empty-icon" />
          <h2 className="brands-page__empty-title">No brands yet</h2>
          <p className="brands-page__empty-desc">
            Set up your brand folder — voice, USPs, banned words, and compliance rules — so every
            banner you generate is on-brand by default.
          </p>
          {canCreate ? (
            <button type="button" onClick={() => navigate("/brands/new")} className="btn btn--primary">
              Create your first brand
            </button>
          ) : (
            <p className="brands-page__empty-desc">Ask an admin to set up a brand to get started.</p>
          )}
        </div>
      ) : (
        <div className="brands-page__grid">
          {brands.map((entry) => (
            <BrandCard
              key={entry.brand.id}
              entry={entry}
              assetCount={assetCounts[entry.brand.id] ?? 0}
              onOpen={() => navigate(`/brands/${entry.brand.slug}`)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function BrandCard({ entry, assetCount, onOpen }) {
  const { brand, references } = entry;
  const referenceCount = (references ?? []).length;
  // logoPath is a legacy field that's always null now that logos are real
  // references — same staleness as referenceCount was. Default to the
  // first logo-kind reference when there are multiple.
  const logoUrl = (references ?? []).find((r) => r.kind === "logo")?.url ?? null;

  return (
    <article
      className="brand-card"
      role="button"
      tabIndex={0}
      onClick={onOpen}
      onKeyDown={(e) => {
        if (e.key === "Enter") onOpen();
      }}
    >
      <div className="brand-card__top">
        {logoUrl ? (
          <img src={logoUrl} alt="" className="brand-card__logo" />
        ) : (
          <span className="brand-card__logo-fallback">
            {brand.name?.charAt(0).toUpperCase() ?? "?"}
          </span>
        )}
      </div>
      <h3 className="brand-card__name">{brand.name}</h3>
      <div className="brand-card__meta">
        <span>{referenceCount} reference{referenceCount === 1 ? "" : "s"}</span>
        <span>{assetCount} asset{assetCount === 1 ? "" : "s"}</span>
      </div>
    </article>
  );
}